import { Grid, makeStyles } from '@material-ui/core';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import QCXFormTitle from '../../../../../components/form-title/QCXFormTitle';
import QCXFinalAlternativeCurrencyField from '../../../../../shared-components/final-currency-field/QCXFinalAlternativeCurrencyField';
import QCXFinalDateTimePickerField from '../../../../../shared-components/final-date-time-picker-field/QCXFinalDateTimePickerField';
import QCXFinalForm from '../../../../../shared-components/final-form/QCXFinalForm';
import QCXFinalNumericDecimalField from '../../../../../shared-components/final-numeric-decimal-field/QCXFinalNumericDecimalField';
import QCXDataGrid from '../../../../../shared-components/data-grid/QCXDataGrid';
import QCXSelectMoedaAutocomplete from '../../../../../shared-components/final-select-moeda-field/QCXSelectMoedaAutocomplete';
import QCXSelectContaBancariaAutocompleteField from '../../../../../shared-components/select-conta-bancaria/QCXSelectContaBancariaAutocompleteField';
import QCXSelectDespesaReceitaAutocomplete from '../../../../../shared-components/select-despesa-receita/QCXSelectDespesaReceitaAutocomplete';
import QCXSelectPagadorAutocomplete from '../../../../../shared-components/select-pagador-autocomplete/QCXSelectPagadorAutocomplete';
import { selectMoedas } from '../../../../../features/moeda/moedaSlice';
import { getCurrentDate } from '../../../../../utils/general/general-utils';
import { OperacaoMovimentacaoUtils } from '../../../../../utils/general/movimentacao/movimentacaoUtils';
import SolicitacaoPagamentoUtils from '../../../../../utils/general/solicitacao-pagamento/SolicitacaoPagamentoUtils';
import { required as requiredValidator } from '../../../../../utils/validators/field/validator';

function TaxaAutoFill({ moedaId, form }) {
  const moedas = useSelector(selectMoedas);

  useEffect(() => {
    const moeda = moedas.find((m) => m.id === moedaId || m.id === Number(moedaId));
    if (moeda?.code === '790' || moeda?.code === 790) {
      form.change('taxa', 1);
    }
  }, [moedaId, moedas]);

  return null;
}

const useStyles = makeStyles(() => ({
  gridFields: {
    paddingBottom: '16px',
  },
  subtitle: {
    margin: '20px 0',
  },
}));

export default function QCXConciliacaoBancariaFinanceiroForm({
  isCreate,
  isConsult,
  isUpdate,
  initialValues,
  tipoConciliacao,
  followUp = null,
  tableRows = [],
  handleSubmit,
}) {
  const { t } = useTranslation();

  const classes = useStyles();

  const internalInitialValues = useMemo(
    () =>
      isCreate
        ? {
          ...initialValues,
          dataSolicitacao: getCurrentDate().toDate(),
          tipo: OperacaoMovimentacaoUtils.ENTRADA,
          status: SolicitacaoPagamentoUtils.SOLICITACAO_PENDENTE,
          followUp: followUp ?? initialValues?.followUp,
        }
        : initialValues,
    [isCreate, initialValues, followUp]
  );

  const temProcesso = useMemo(() => tableRows.length > 0, [tableRows]);

  // mesmas colunas do cabeçalho da solicitação de pagamento operacional
  const columns = useMemo(
    () => [
      {
        field: 'processo',
        headerName: t('com.muralis.qcx.processo'),
        flex: 50,
        type: 'string',
        headerAlign: 'center',
        align: 'center',
        valueGetter: ({ row }) => row?.processo || '-',
      },
      {
        field: 'cliente',
        headerName: t('com.muralis.qcx.cliente.label'),
        flex: 170,
        type: 'string',
        headerAlign: 'center',
        align: 'center',
        valueGetter: ({ row }) => row?.cliente || '-',
      },
      {
        field: 'servico',
        headerName: t('com.muralis.qcx.servico.labelSingular'),
        flex: 100,
        type: 'string',
        valueGetter: ({ row }) => row?.servico || '-',
      },
      {
        field: 'viaTransporte',
        headerName: t('com.muralis.qcx.transporte.viaTransporte'),
        flex: 100,
        type: 'string',
        headerAlign: 'center',
        align: 'center',
        valueGetter: ({ row }) => row?.viaTransporte || '-',
      },
    ],
    []
  );

  const configureDespesaReceitaFilterPredicateWith = useMemo(() => {
    if (tipoConciliacao === 'CREDITO') {
      return [
        {
          name: 'credito',
          value: 'CREDITO',
        },
      ];
    }

    if (tipoConciliacao === 'DEBITO') {
      return [
        {
          name: 'despesa',
          value: 'DESPESA',
        },
        {
          name: 'operacional',
          value: false,
        },
      ];
    }

    return [];
  }, [tipoConciliacao]);

  return (
    <QCXFinalForm
      isConsult={isConsult}
      isUpdate={isUpdate}
      initialValues={internalInitialValues}
      handleSubmitValues={handleSubmit}
    >
      {({ form, values }) => (
        <>
          <TaxaAutoFill moedaId={values?.moeda?.id} form={form} />
          <Grid container spacing={3}>
            {/* GRID DO PROCESSO (operacional) */}
            {temProcesso && (
              <Grid item className={classes.gridFields} xs={12} sm={12} md={12}>
                <QCXDataGrid columns={columns} rows={tableRows} />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <Grid item className={classes.gridFields} xs={12} sm={6} md={8}>
                <QCXFinalDateTimePickerField
                  id="date-picker-data-solicitacao-field"
                  key="date-picker-data-solicitacao-field"
                  name="dataSolicitacao"
                  label={t('com.muralis.qcx.numerarioImportacao.dataSolicitacao')}
                  disabled
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <QCXFormTitle
                title={t('com.muralis.qcx.despesa.labelPlural')}
                boxProps={{
                  pt: 1,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <QCXSelectDespesaReceitaAutocomplete
                id="autocomplete-select-field-despesa"
                key="autocomplete-select-field-despesa"
                name="despesaReceita.id"
                label={t('com.muralis.qcx.despesa.labelPlural')}
                initialValues={values}
                disabled={isConsult}
                fieldProps={{ validate: requiredValidator }}
                filterParams={configureDespesaReceitaFilterPredicateWith}
                required
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <QCXSelectMoedaAutocomplete
                id="select-field-moeda-seguro"
                key="select-field-moeda-seguro"
                name="moeda.id"
                label={t('com.muralis.qcx.moeda.label')}
                disabled={isConsult}
                initialValues={values}
                fieldProps={{ validate: requiredValidator }}
                defaultCode={SolicitacaoPagamentoUtils.isAdministrativa(values?.tipo) ? '790' : undefined}
                required
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <QCXFinalNumericDecimalField
                id="taxa-cambio-field"
                key="taxa-cambio-field"
                name="taxa"
                label="TAXA DE CÂMBIO"
                scale={7}
                disabled={isConsult}
                fieldProps={{ validate: requiredValidator }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <QCXSelectPagadorAutocomplete
                id="select-pagador-field"
                key="select-pagador-field"
                name="pagador"
                label={tipoConciliacao === 'CREDITO' ? 'PAGADOR' : 'FAVORECIDO'}
                disabled={isConsult}
                fieldProps={{ validate: requiredValidator }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <QCXSelectContaBancariaAutocompleteField
                id="autocomplete-select-conta-bancaria-pagamento-field"
                key="autocomplete-select-conta-bancaria-pagamento-field"
                name="contaBancaria"
                label={t('com.muralis.qcx.conta.contaCorrente')}
                initialValues={values}
                disabled={isConsult}
                fieldProps={{ validate: requiredValidator }}
                filtraControlaSaldo
                required
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <QCXFinalAlternativeCurrencyField
                id="financeiro-valor-receber-field"
                key="financeiro-valor-receber-field"
                name="valorAReceber"
                label={tipoConciliacao === 'CREDITO' ? 'VALOR A RECEBER' : 'VALOR A PAGAR'}
                disabled={isConsult}
                required
              />
            </Grid>
          </Grid>
        </>
      )}
    </QCXFinalForm>
  );
}
